import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Table,
  Select,
  Switch,
  Alert,
  Button,
  Checkbox,
  Form,
  Tooltip,
} from 'antd';
import { WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;

// Helper: Check if color is too bright and return readable text color
const getReadableTextColor = (hexColor: string | undefined): string => {
  if (!hexColor) return '#000';

  // Remove # if present
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6) return '#000';

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (perceived brightness)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // If too bright (> 0.7), invert the color
  if (luminance > 0.7) {
    const invR = 255 - r;
    const invG = 255 - g;
    const invB = 255 - b;
    return `rgb(${invR}, ${invG}, ${invB})`;
  }

  return hexColor;
};

// Custom styled checkbox component with color support
const ColoredCheckbox: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  color: string;
  disabled?: boolean;
}> = ({ checked, onChange, color, disabled = false }) => {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onChange(!checked)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === ' ' || e.key === 'Enter')) {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      style={{
        width: 12,
        height: 12,
        borderRadius: 3,
        border: `1.5px solid ${disabled ? '#d9d9d9' : color}`,
        backgroundColor: checked
          ? disabled
            ? '#d9d9d9'
            : color
          : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {checked && (
        <svg
          width="10"
          height="8"
          viewBox="0 0 10 8"
          fill="none"
          style={{ display: 'block' }}
        >
          <path
            d="M1 4L3.5 6.5L9 1"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
};

interface LayerInfo {
  name: string;
  featureCount: number;
  color: string;
  visible: boolean;
  frozen: boolean;
  inUse?: boolean;
  lineType?: string;
  locked?: boolean;
  isAllRow?: boolean; // Marker for "All Layers" row
}

interface AnalysisResult {
  name: string;
  unit: string;
  viewPort: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  layers: LayerInfo[];
  totalFeatures: number;
}

interface DrawingConfigModalProps {
  open: boolean;
  analysisResult: AnalysisResult | null;
  onConfirm: (config: {
    sourceUnit?: string;
    excludeLayers: string[];
    hideLayers: string[];
  }) => void;
  onCancel: () => void;
}

export const DrawingConfigModal: React.FC<DrawingConfigModalProps> = ({
  open,
  analysisResult,
  onConfirm,
  onCancel,
}) => {
  const [selectedUnit, setSelectedUnit] = useState<string | undefined>(
    undefined,
  );
  // included = Upload toggle (ON = uploaded, OFF = excluded)
  // visible = Visible checkbox (checked = visible, unchecked = hidden)
  const [layerStates, setLayerStates] = useState<
    Record<string, { included: boolean; visible: boolean }>
  >({});

  // Initialize layer states when analysis result changes
  useEffect(() => {
    if (analysisResult?.layers) {
      const initialStates: Record<
        string,
        { included: boolean; visible: boolean }
      > = {};
      analysisResult.layers.forEach((layer) => {
        initialStates[layer.name] = {
          included: !layer.frozen, // Toggle: frozen → OFF (excluded)
          visible: layer.visible, // Checkbox: visible from JSON
        };
      });
      setLayerStates(initialStates);

      // Set unit if detected
      if (analysisResult.unit && analysisResult.unit !== 'none') {
        setSelectedUnit(analysisResult.unit);
      } else {
        setSelectedUnit(undefined);
      }
    }
  }, [analysisResult]);

  // Calculate totals
  const { includedFeatureCount, includedLayerCount } = useMemo(() => {
    if (!analysisResult?.layers) {
      return {
        includedFeatureCount: 0,
        includedLayerCount: 0,
      };
    }

    let included = 0;
    let layerCount = 0;

    analysisResult.layers.forEach((layer) => {
      if (layerStates[layer.name]?.included) {
        included += layer.featureCount;
        layerCount++;
      }
    });

    return {
      includedFeatureCount: included,
      includedLayerCount: layerCount,
    };
  }, [analysisResult, layerStates]);

  // Check if all layers are visible (for All Layers checkbox)
  const allLayersVisible = useMemo(() => {
    const states = Object.values(layerStates);
    if (states.length === 0) return true;
    // Only consider included layers for visibility check
    const includedStates = Object.entries(layerStates).filter(
      ([, state]) => state.included,
    );
    return includedStates.every(([, state]) => state.visible);
  }, [layerStates]);

  // Check if some layers are visible (for indeterminate state)
  const someLayersVisible = useMemo(() => {
    const includedStates = Object.entries(layerStates).filter(
      ([, state]) => state.included,
    );
    return (
      includedStates.some(([, state]) => state.visible) &&
      !includedStates.every(([, state]) => state.visible)
    );
  }, [layerStates]);

  // Check if all layers are included (for All Layers toggle)
  const allLayersIncluded = useMemo(() => {
    const states = Object.values(layerStates);
    if (states.length === 0) return true;
    return states.every((state) => state.included);
  }, [layerStates]);

  // Sort layers by feature count descending and add "All Layers" row at top
  const tableData = useMemo(() => {
    if (!analysisResult?.layers) return [];

    const sorted = [...analysisResult.layers].sort(
      (a, b) => b.featureCount - a.featureCount,
    );

    // Create "All Layers" summary row
    const allLayersRow: LayerInfo = {
      name: 'All Layers',
      featureCount: includedFeatureCount,
      color: '#000',
      visible: true,
      frozen: false,
      isAllRow: true,
    };

    return [allLayersRow, ...sorted];
  }, [analysisResult, includedFeatureCount]);

  // Validation
  const unitRequired = analysisResult?.unit === 'none';
  const unitValid = !unitRequired || !!selectedUnit;
  const hasIncludedLayers = includedLayerCount > 0;
  const isLargeFile = includedFeatureCount > 150000;
  const isVeryLargeFile = includedFeatureCount > 250000;
  const isValid = unitValid && hasIncludedLayers; // No longer blocking upload for large files

  // Toggle all layers visibility (checkbox)
  const handleToggleAllVisibility = (checked: boolean) => {
    const newStates = { ...layerStates };
    Object.keys(newStates).forEach((name) => {
      // Only change visibility for included layers
      if (newStates[name].included) {
        newStates[name] = { ...newStates[name], visible: checked };
      }
    });
    setLayerStates(newStates);
  };

  // Toggle all layers included (upload toggle)
  const handleToggleAllIncluded = (checked: boolean) => {
    const newStates = { ...layerStates };
    Object.keys(newStates).forEach((name) => {
      newStates[name] = { ...newStates[name], included: checked };
    });
    setLayerStates(newStates);
  };

  const handleConfirm = () => {
    const excludeLayers: string[] = [];
    const hideLayers: string[] = [];

    // Toggle controls excludeLayers, Checkbox controls hideLayers
    Object.entries(layerStates).forEach(([name, state]) => {
      if (!state.included) {
        // Toggle OFF = layer completely excluded from GeoJSON output
        excludeLayers.push(name);
      } else if (!state.visible) {
        // Toggle ON + Checkbox OFF = layer included but marked as hidden
        hideLayers.push(name);
      }
      // Toggle ON + Checkbox ON = layer fully included and visible (no action needed)
    });

    onConfirm({
      sourceUnit: unitRequired ? selectedUnit : undefined,
      excludeLayers,
      hideLayers,
    });
  };

  const columns: ColumnsType<LayerInfo> = [
    {
      title: (
        <Tooltip
          title="Set layer visibility on the map after upload"
          overlayStyle={{ maxWidth: 300 }}
        >
          Visible
        </Tooltip>
      ),
      dataIndex: 'visible',
      key: 'visible',
      width: 70,
      align: 'center',
      render: (_, record) => {
        if (record.isAllRow) {
          // "All Layers" row: checkbox controls all visibility
          return (
            <Checkbox
              checked={allLayersVisible}
              indeterminate={someLayersVisible}
              disabled={!hasIncludedLayers}
              onChange={(e) => handleToggleAllVisibility(e.target.checked)}
            />
          );
        }
        const isIncluded = layerStates[record.name]?.included ?? true;
        const checkboxColor = getReadableTextColor(record.color);
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ColoredCheckbox
              checked={layerStates[record.name]?.visible ?? true}
              disabled={!isIncluded}
              onChange={(checked) =>
                setLayerStates((prev) => ({
                  ...prev,
                  [record.name]: { ...prev[record.name], visible: checked },
                }))
              }
              color={checkboxColor}
            />
          </div>
        );
      },
    },
    {
      title: <Tooltip title="Layer name from DWG file">Layer Name</Tooltip>,
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => {
        if (record.isAllRow) {
          return <span style={{ fontWeight: 600, color: '#000' }}>{name}</span>;
        }
        const isIncluded = layerStates[record.name]?.included ?? true;
        const maxLen = 35;
        const isTruncated = name.length >= maxLen;
        const displayName = isTruncated ? `${name.slice(0, maxLen)}...` : name;

        const content = (
          <span
            style={{
              color: isIncluded ? 'inherit' : '#bbb',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {displayName}
          </span>
        );

        return isTruncated ? (
          <Tooltip title={name} overlayStyle={{ maxWidth: 500 }}>
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    },
    {
      title: (
        <Tooltip title="Number of features (lines, shapes, text) in layer">
          Features
        </Tooltip>
      ),
      dataIndex: 'featureCount',
      key: 'featureCount',
      width: 100,
      render: (count, record) => {
        if (record.isAllRow) {
          const isTooMany = count > 250000;
          return (
            <span
              style={{
                fontWeight: 600,
                color: isTooMany ? '#ff4d4f' : 'inherit',
              }}
            >
              {count.toLocaleString()}
            </span>
          );
        }
        const isIncluded = layerStates[record.name]?.included ?? true;
        const isLarge = count > 100000;
        return (
          <span
            style={{
              color: !isIncluded ? '#bbb' : isLarge ? '#ff4d4f' : 'inherit',
            }}
          >
            {count.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: (
        <Tooltip
          title={
            <>
              Exclude this layer from the upload.
              <br />
              The original DWG file is not affected
            </>
          }
          overlayStyle={{ maxWidth: 400 }}
        >
          Upload
        </Tooltip>
      ),
      dataIndex: 'upload',
      key: 'upload',
      width: 80,
      align: 'center',
      render: (_, record) => {
        if (record.isAllRow) {
          // "All Layers" row: toggle controls all include/exclude
          return (
            <Switch
              size="small"
              checked={allLayersIncluded}
              onChange={handleToggleAllIncluded}
            />
          );
        }
        return (
          <Switch
            size="small"
            checked={layerStates[record.name]?.included ?? true}
            onChange={(checked) =>
              setLayerStates((prev) => ({
                ...prev,
                [record.name]: { ...prev[record.name], included: checked },
              }))
            }
          />
        );
      },
    },
  ];

  return (
    <Modal
      title="Drawing Settings"
      open={open}
      onCancel={onCancel}
      width={600}
      centered
      footer={null}
      destroyOnClose
    >
      <Form
        layout="vertical"
        className="common-form"
        name="config-drawing-form"
        autoComplete="off"
      >
        {/* Units Error - above selector (no unit selected) */}
        {unitRequired && !selectedUnit && (
          <Alert
            message="This DWG does not contain unit information. Please select the drawing units to continue."
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined style={{ fontSize: 24 }} />}
            style={{ marginBottom: 12 }}
          />
        )}
        {/* Units Warning - above selector (unit selected, verify after upload) */}
        {unitRequired && selectedUnit && (
          <Alert
            message={`Please verify that unit ${
              selectedUnit === 'm'
                ? 'Meters'
                : selectedUnit === 'ft'
                ? 'Feet'
                : selectedUnit === 'in'
                ? 'Inches'
                : selectedUnit === 'cm'
                ? 'Centimeters'
                : selectedUnit === 'mm'
                ? 'Millimeters'
                : selectedUnit
            } is correct after uploading.`}
            type="warning"
            showIcon
            icon={<WarningOutlined style={{ fontSize: 24 }} />}
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Units Section */}
        <Form.Item
          label={<span style={{ fontWeight: 600 }}>Units</span>}
          required={false}
          validateStatus={unitRequired && !selectedUnit ? 'error' : undefined}
        >
          {unitRequired ? (
            <Select
              style={{ width: 200 }}
              placeholder="Select unit"
              value={selectedUnit}
              onChange={setSelectedUnit}
              suffixIcon={
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="#666"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              <Option value="m">Meters (m)</Option>
              <Option value="ft">Feet (ft)</Option>
              <Option value="in">Inches (in)</Option>
              <Option value="cm">Centimeters (cm)</Option>
              <Option value="mm">Millimeters (mm)</Option>
            </Select>
          ) : (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minWidth: 140,
                height: 32,
                padding: '4px 16px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                backgroundColor: '#fafafa',
                fontSize: 14,
                color: '#333',
              }}
            >
              {selectedUnit === 'm'
                ? 'Meters (m)'
                : selectedUnit === 'ft'
                ? 'Feet (ft)'
                : selectedUnit === 'in'
                ? 'Inches (in)'
                : selectedUnit === 'cm'
                ? 'Centimeters (cm)'
                : selectedUnit === 'mm'
                ? 'Millimeters (mm)'
                : selectedUnit || 'Unknown'}
            </div>
          )}
        </Form.Item>

        {/* Spacer between Units and Layers */}
        <div style={{ height: 24 }} />

        {/* Layers Warnings - above layers table */}
        {isLargeFile && (
          <Alert
            message={
              <>
                <strong>Large number of features detected</strong>
                <br />
                Uploading may slow down the app and could cause instability or
                crashes.
                {isVeryLargeFile && (
                  <>
                    <br />
                    We recommend removing unnecessary layers before uploading.
                  </>
                )}
              </>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined style={{ fontSize: 24 }} />}
            style={{ marginBottom: 12 }}
          />
        )}

        {/* Layers Section */}
        <Form.Item
          label={
            <div>
              <span style={{ fontWeight: 600 }}>Layers</span>
              <br />
              <span style={{ fontWeight: 400, fontSize: 13, color: '#666' }}>
                ({includedLayerCount} of {analysisResult?.layers?.length || 0}{' '}
                selected, {includedFeatureCount.toLocaleString()} features)
              </span>
            </div>
          }
          validateStatus={!hasIncludedLayers ? 'error' : undefined}
          help={
            !hasIncludedLayers
              ? 'At least one layer must be included.'
              : undefined
          }
        >
          <Table
            columns={columns}
            dataSource={tableData}
            rowKey={(record) =>
              record.isAllRow ? '__all_layers__' : record.name
            }
            size="small"
            pagination={false}
            scroll={{ y: 250 }}
            className="layers-table"
            rowClassName={(record) => (record.isAllRow ? 'all-layers-row' : '')}
          />
          <style>{`
            .layers-table .ant-table-body::-webkit-scrollbar {
              display: none;
            }
            .layers-table .ant-table-body {
              -ms-overflow-style: none;
              scrollbar-width: none;
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
            /* Header row */
            .layers-table .ant-table-thead > tr > th {
              background-color: #F0F0F0 !important;
            }
            .layers-table .ant-table-thead > tr > th:hover {
              background-color: #E8EAED !important;
            }
            /* Remove header cell gaps */
            .layers-table .ant-table-thead > tr > th::before {
              display: none !important;
            }
            /* All Layers row */
            .layers-table .all-layers-row td {
              background-color: #F2F4F7 !important;
              border-top: 2px solid #D0D7DE !important;
              border-bottom: 2px solid #D0D7DE !important;
            }
            .layers-table .all-layers-row:hover td {
              background-color: #E6EBF1 !important;
            }
            /* Regular rows */
            .layers-table .ant-table-tbody > tr > td {
              background-color: #FFFFFF;
            }
            .layers-table .ant-table-tbody > tr:hover > td {
              background-color: #F5F7FA !important;
            }
            /* Override for All Layers row to keep its colors on hover */
            .layers-table .ant-table-tbody > tr.all-layers-row:hover > td {
              background-color: #E6EBF1 !important;
            }
          `}</style>
        </Form.Item>

        {/* Spacer between Layers and Buttons */}
        <div style={{ height: 6 }} />

        <div className="form-btns">
          <Button className="self-btn" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="common-btn"
            disabled={!isValid}
            onClick={handleConfirm}
          >
            Upload
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default DrawingConfigModal;
